/**
 * useUangJalan — REQ-090 state machine for the admin Uang Jalan Operational
 * View (ODE-P07-T08).
 *
 * Flow: `pending` (filters not yet submitted) → `submit()` → `loading` → the
 * guarded list outcome:
 *   200 → `ready` (server page; an empty `rows` array is the REQ-090 empty
 *         state rendered by the module, never a fabricated row)
 *   400 → `error`   (invalid filter — the server rejected it, retryable with
 *         corrected input)
 *   401 → `unauthorized`
 *   403 → `blocked` (server-enforced; the client mirror is UX only)
 *   503/network → `error`
 *
 * Detail (`openDetail(entityId)`) is a separate read-only projection with its
 * own state (`idle`/`loading`/`ready`/`not_found`/`unauthorized`/`blocked`/`error`).
 * Nothing here computes the gate, the status or the control state: every value
 * (including the REQ-011 `progressionAllowed` flag and the ODE-P06-T04
 * duplicate/reversal state) comes from the server projection verbatim, and no
 * mutation action is ever offered.
 *
 * `loadMore()` appends the next keyset page (the cursor is opaque; the client
 * never builds one).
 *
 * Traces to: ODE-PRD-REQ-011, ODE-PRD-REQ-071, ODE-PRD-REQ-072,
 *            ODE-PRD-REQ-076, ODE-PRD-REQ-090.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import {
  api,
  type AdminUangJalanDetail,
  type AdminUangJalanDetailResult,
  type AdminUangJalanListResult,
  type AdminUangJalanPage,
  type AdminUangJalanQuery,
} from '../../api/client.js';

/** The raw filter form (all values are strings; empties are omitted). */
export interface UangJalanForm {
  readonly doNumber: string;
  readonly status: string;
  readonly from: string;
  readonly to: string;
  readonly actorId: string;
  readonly reversalEligible: string;
  readonly reversed: string;
  readonly limit: string;
}

export const EMPTY_UANG_JALAN_FORM: UangJalanForm = {
  doNumber: '',
  status: '',
  from: '',
  to: '',
  actorId: '',
  reversalEligible: '',
  reversed: '',
  limit: '',
};

export type UangJalanListState =
  | { readonly kind: 'pending' }
  | { readonly kind: 'loading' }
  | { readonly kind: 'ready'; readonly page: AdminUangJalanPage; readonly loadingMore: boolean }
  | { readonly kind: 'unauthorized' }
  | { readonly kind: 'blocked'; readonly message: string }
  | { readonly kind: 'error'; readonly message: string };

export type UangJalanDetailState =
  | { readonly kind: 'idle' }
  | { readonly kind: 'loading'; readonly entityId: string }
  | { readonly kind: 'ready'; readonly detail: AdminUangJalanDetail }
  | { readonly kind: 'not_found' }
  | { readonly kind: 'unauthorized' }
  | { readonly kind: 'blocked'; readonly message: string }
  | { readonly kind: 'error'; readonly message: string };

export interface UseUangJalanOptions {
  readonly token: string | null;
  /** Test seam: list fetch (default: the shared api client). */
  readonly list?:
    | ((query: AdminUangJalanQuery, token: string) => Promise<AdminUangJalanListResult>)
    | undefined;
  /** Test seam: detail fetch (default: the shared api client). */
  readonly detail?:
    | ((entityId: string, token: string) => Promise<AdminUangJalanDetailResult>)
    | undefined;
}

export interface UseUangJalanResult {
  readonly form: UangJalanForm;
  readonly setForm: (patch: Partial<UangJalanForm>) => void;
  readonly submit: () => void;
  readonly reset: () => void;
  readonly loadMore: () => void;
  readonly state: UangJalanListState;
  readonly detailState: UangJalanDetailState;
  readonly openDetail: (entityId: string) => void;
  readonly closeDetail: () => void;
}

/** Maps the raw form to the wire query (empties omitted, limit as a number). */
export function toUangJalanQuery(form: UangJalanForm): AdminUangJalanQuery {
  const query: Record<string, string | number | boolean> = {};
  if (form.doNumber.trim() !== '') query.doNumber = form.doNumber.trim();
  if (form.status.trim() !== '') query.status = form.status.trim();
  if (form.from.trim() !== '') query.from = form.from.trim();
  if (form.to.trim() !== '') query.to = form.to.trim();
  if (form.actorId.trim() !== '') query.actorId = form.actorId.trim();
  if (form.reversalEligible === 'true') query.reversalEligible = true;
  if (form.reversalEligible === 'false') query.reversalEligible = false;
  if (form.reversed === 'true') query.reversed = true;
  if (form.reversed === 'false') query.reversed = false;
  const limit = Number.parseInt(form.limit, 10);
  if (form.limit.trim() !== '' && Number.isInteger(limit)) query.limit = limit;
  return query;
}

export function useUangJalan(options: UseUangJalanOptions): UseUangJalanResult {
  const { token } = options;
  const [form, setFormState] = useState<UangJalanForm>(EMPTY_UANG_JALAN_FORM);
  const [state, setState] = useState<UangJalanListState>({ kind: 'pending' });
  const [detailState, setDetailState] = useState<UangJalanDetailState>({ kind: 'idle' });
  const [request, setRequest] = useState<{
    readonly attempt: number;
    readonly query: AdminUangJalanQuery;
  } | null>(null);
  const attemptRef = useRef(0);
  const stateRef = useRef<UangJalanListState>(state);
  stateRef.current = state;

  const defaultList = (query: AdminUangJalanQuery, tokenValue: string) =>
    api.listUangJalan(query, tokenValue);
  const defaultDetail = (entityId: string, tokenValue: string) =>
    api.fetchUangJalanDetail(entityId, tokenValue);
  const listRef = useRef(options.list ?? defaultList);
  listRef.current = options.list ?? defaultList;
  const detailRef = useRef(options.detail ?? defaultDetail);
  detailRef.current = options.detail ?? defaultDetail;

  const setForm = useCallback((patch: Partial<UangJalanForm>) => {
    setFormState((current) => ({ ...current, ...patch }));
  }, []);

  const submit = useCallback(() => {
    attemptRef.current += 1;
    setRequest({ attempt: attemptRef.current, query: toUangJalanQuery(form) });
  }, [form]);

  const reset = useCallback(() => {
    setFormState(EMPTY_UANG_JALAN_FORM);
    setRequest(null);
    setState({ kind: 'pending' });
    setDetailState({ kind: 'idle' });
  }, []);

  // Initial page load (a fresh submit replaces the list; loadMore appends).
  useEffect(() => {
    if (request === null) return undefined;
    if (token === null) {
      setState({ kind: 'unauthorized' });
      return undefined;
    }
    let cancelled = false;
    setState({ kind: 'loading' });
    listRef
      .current(request.query, token)
      .then((result) => {
        if (cancelled) return;
        setState(mapListResult(result));
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        setState({
          kind: 'error',
          message: error instanceof Error ? error.message : 'Network error',
        });
      });
    return () => {
      cancelled = true;
    };
  }, [token, request]);

  const loadMore = useCallback(() => {
    const current = stateRef.current;
    if (current.kind !== 'ready' || !current.page.hasMore || current.page.nextCursor === null) {
      return;
    }
    const cursor = current.page.nextCursor;
    const baseQuery = request?.query ?? {};
    setState({ ...current, loadingMore: true });
    void (async () => {
      if (token === null) return;
      try {
        const result = await listRef.current({ ...baseQuery, cursor }, token);
        if (result.status === 200 && result.payload !== null) {
          setState((latest) => {
            if (latest.kind !== 'ready') return latest;
            return {
              kind: 'ready',
              loadingMore: false,
              page: {
                ...result.payload!,
                rows: [...latest.page.rows, ...result.payload!.rows],
              },
            };
          });
        } else {
          setState({ kind: 'error', message: result.message ?? `HTTP ${result.status}` });
        }
      } catch (error: unknown) {
        setState({
          kind: 'error',
          message: error instanceof Error ? error.message : 'Network error',
        });
      }
    })();
  }, [request, token]);

  const openDetail = useCallback(
    (entityId: string) => {
      if (token === null) {
        setDetailState({ kind: 'unauthorized' });
        return;
      }
      setDetailState({ kind: 'loading', entityId });
      void detailRef
        .current(entityId, token)
        .then((result) => {
          if (result.status === 200 && result.payload !== null) {
            setDetailState({ kind: 'ready', detail: result.payload });
          } else if (result.status === 404) {
            setDetailState({ kind: 'not_found' });
          } else if (result.status === 401) {
            setDetailState({ kind: 'unauthorized' });
          } else if (result.status === 403) {
            setDetailState({ kind: 'blocked', message: result.message ?? 'Forbidden' });
          } else {
            setDetailState({ kind: 'error', message: result.message ?? `HTTP ${result.status}` });
          }
        })
        .catch((error: unknown) => {
          setDetailState({
            kind: 'error',
            message: error instanceof Error ? error.message : 'Network error',
          });
        });
    },
    [token],
  );

  const closeDetail = useCallback(() => {
    setDetailState({ kind: 'idle' });
  }, []);

  return { form, setForm, submit, reset, loadMore, state, detailState, openDetail, closeDetail };
}

function mapListResult(result: AdminUangJalanListResult): UangJalanListState {
  switch (result.status) {
    case 200:
      return result.payload === null
        ? { kind: 'error', message: 'Respons daftar Uang Jalan tidak valid.' }
        : { kind: 'ready', page: result.payload, loadingMore: false };
    case 401:
      return { kind: 'unauthorized' };
    case 403:
      return { kind: 'blocked', message: result.message ?? 'Forbidden' };
    default:
      return { kind: 'error', message: result.message ?? `HTTP ${result.status}` };
  }
}
